import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface CourseDirectorySkeletonProps {
  // Number of placeholder cards to render in the results grid.
  count?: number;
}

// Initial-load placeholder for the catalog: a sidebar skeleton plus a grid of
// card skeletons matching the real layout (aspect-video thumbnail, up to
// xl:grid-cols-4). Neutral design-system styling, dark-mode aware.
export function CourseDirectorySkeleton({
  count = 8,
}: CourseDirectorySkeletonProps) {
  return (
    <div className="flex flex-col md:flex-row gap-6">
      {/* Sidebar skeleton (desktop only) */}
      <div className="hidden md:block w-72 shrink-0">
        <Skeleton className="mb-4 h-10 w-full" />
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-5">
          {Array.from({ length: 3 }).map((_, section) => (
            <div className="space-y-2" key={section}>
              <Skeleton className="h-4 w-20" />
              {Array.from({ length: 4 }).map((_, row) => (
                <div className="flex items-center gap-2" key={row}>
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-28" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Results grid skeleton */}
      <div className="flex-1 min-w-0">
        <Skeleton className="mb-4 h-4 w-24" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: count }).map((_, i) => (
            <div
              className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800"
              key={i}
            >
              <Skeleton className="aspect-video w-full rounded-none" />
              <div className="space-y-3 p-4">
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <div className="flex items-center gap-4 pt-2">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3 w-8" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CourseDirectorySkeleton;
